import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  User, 
  Users, 
  Calendar, 
  DollarSign, 
  CheckCircle,
  AlertCircle,
  Mail,
  Phone,
  MapPin,
  Baby,
  Save,
  Copy,
  Check,
  Search,
  Download,
  AlertTriangle,
  RefreshCw,
  Eye,
  FileText
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import html2canvas from "html2canvas";
import { formatAdminError } from "../lib/adminError";
import KitSelectionStep from "../components/KitSelectionStep";

axios.defaults.withCredentials = true;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL_CHILD = {
  given_name: "",
  last_name: "",
  birthdate: "",
  gender: "male",
  nationality: "",
  school: "",
  email: "",
  phone: "",
  medical_conditions: "",
  emergency_contact_name: "",
  emergency_contact: "",
  emergency_relationship: "Parent",
  session_ids: [],
  is_existing: false,
  existing_id: null,
  // Feb 2026 fix — admin opts in per child for the Sign-up Registration
  // Fee. Default true so brand-new players are charged automatically;
  // admin unticks for re-enrollments / kids who have already paid it.
  charge_registration_fee: true,
};

export default function AdminRegisterForParent() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Duplicate-child prompt (Feb 2026): { code, message, duplicate_child_names,
  //   new_child_names, registration_id, amount, created_at, status, payment_status }
  const [duplicatePrompt, setDuplicatePrompt] = useState(null);

  // Email lookup state
  const [lookupEmail, setLookupEmail] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [showExistingChildren, setShowExistingChildren] = useState(false);

  // Parent info
  const [parentGivenName, setParentGivenName] = useState("");
  const [parentLastName, setParentLastName] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [parentPhone, setParentPhone] = useState("");
  const [parentAddress, setParentAddress] = useState("");
  const [parentNationality, setParentNationality] = useState("");
  const [isSccMember, setIsSccMember] = useState(false);
  const [membershipNumber, setMembershipNumber] = useState("");
  // Jun 2026 — SCC membership number format validation. The club member
  // discount is granted ONLY when the number validates (checkbox alone is not
  // enough). `membershipValid`: null = not yet checked, true/false = result.
  const [membershipValid, setMembershipValid] = useState(null);
  const [membershipValidating, setMembershipValidating] = useState(false);
  const [membershipError, setMembershipError] = useState("");
  // Family-membership numbers (…HFM/WFM/FFM/MFM) reference the PRIMARY SCC
  // member's surname. Admin supplies that member's last name to validate.
  const [primaryMemberLastName, setPrimaryMemberLastName] = useState("");
  const [existingUserId, setExistingUserId] = useState(null);

  // Children
  const [children, setChildren] = useState([{ ...INITIAL_CHILD }]);
  const [duplicateWarnings, setDuplicateWarnings] = useState({});

  // Term & Sessions
  const [terms, setTerms] = useState([]);
  const [selectedTermId, setSelectedTermId] = useState("");
  const [sessions, setSessions] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState(null);

  // Payment
  const [paymentStatus, setPaymentStatus] = useState("pending");
  const [paymentMethod, setPaymentMethod] = useState("paynow");
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");

  // Options
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  const [adminNotes, setAdminNotes] = useState("");

  // Feb 2026 fix — Digital Membership toggle (per-parent). Mirrors the
  // parent self-serve flow. The price comes from the term's rate table
  // (rate_table.digital_membership_config.price).
  const [purchaseDigitalMembership, setPurchaseDigitalMembership] = useState(false);
  const [parentAlreadyHasDM, setParentAlreadyHasDM] = useState(false);
  // Whether the looked-up parent is ALREADY SCC-verified in the system. The
  // authoritative backend charge applies the club 10% whenever this is true —
  // so the on-screen quote must honour it too, or the quote won't match the
  // invoice (this caused a $329 quote vs $296.10 charge mismatch).
  const [parentStoredVerified, setParentStoredVerified] = useState(false);
  // A number this portal generated when the account was approved
  // (SCC-1DF85499 style). It is not a club number, so it must not sit in the
  // membership field pretending to be one.
  const [parentPortalRef, setParentPortalRef] = useState("");

  // Rate table
  const [rateTable, setRateTable] = useState(null);

  // Fee calculation with breakdown
  const [feeBreakdown, setFeeBreakdown] = useState(null);
  const [calculatedFee, setCalculatedFee] = useState(0);
  // Kit chosen at registration (free bundle sizes + paid extras)
  const [kitSelections, setKitSelections] = useState({});
  const [kitExtrasTotal, setKitExtrasTotal] = useState(0);
  const kitChildrenList = useMemo(
    () => children
      .map((c, idx) => ({
        key: c.existing_id || `new-${idx}`,
        name: `${c.given_name || ""} ${c.last_name || ""}`.trim(),
        playerId: c.existing_id || null,
        hasSessions: (c.session_ids || []).length > 0,
      }))
      .filter((c) => c.name && c.hasSessions),
    [JSON.stringify(children.map((c) => ({ n: `${c.given_name}${c.last_name}`, e: c.existing_id, s: (c.session_ids || []).length })))]
  );

  // Review mode
  const [showReview, setShowReview] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);

  // Success dialog state
  const [successDialog, setSuccessDialog] = useState({ open: false, data: null });
  const [copiedField, setCopiedField] = useState(null);
  
  // Ref for QR code download
  const paymentQrRef = useRef(null);
  const [downloadingQr, setDownloadingQr] = useState(false);

  // Download QR code as image
  const downloadPaymentQR = async () => {
    if (!paymentQrRef.current) return;
    
    setDownloadingQr(true);
    try {
      const canvas = await html2canvas(paymentQrRef.current, {
        backgroundColor: '#1e293b',
        scale: 2,
        logging: false,
        useCORS: true
      });
      
      const link = document.createElement('a');
      link.download = `SCC_Payment_${paymentReference}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success("Payment QR saved!");
    } catch (error) {
      console.error("Error downloading QR:", error);
      toast.error("Failed to save QR image");
    } finally {
      setDownloadingQr(false);
    }
  };

  // CRC16 lookup table for PayNow QR code
  const crcTable = [
    0x0000, 0x1021, 0x2042, 0x3063, 0x4084, 0x50a5,
    0x60c6, 0x70e7, 0x8108, 0x9129, 0xa14a, 0xb16b,
    0xc18c, 0xd1ad, 0xe1ce, 0xf1ef, 0x1231, 0x0210,
    0x3273, 0x2252, 0x52b5, 0x4294, 0x72f7, 0x62d6,
    0x9339, 0x8318, 0xb37b, 0xa35a, 0xd3bd, 0xc39c,
    0xf3ff, 0xe3de, 0x2462, 0x3443, 0x0420, 0x1401,
    0x64e6, 0x74c7, 0x44a4, 0x5485, 0xa56a, 0xb54b,
    0x8528, 0x9509, 0xe5ee, 0xf5cf, 0xc5ac, 0xd58d,
    0x3653, 0x2672, 0x1611, 0x0630, 0x76d7, 0x66f6,
    0x5695, 0x46b4, 0xb75b, 0xa77a, 0x9719, 0x8738,
    0xf7df, 0xe7fe, 0xd79d, 0xc7bc, 0x48c4, 0x58e5,
    0x6886, 0x78a7, 0x0840, 0x1861, 0x2802, 0x3823,
    0xc9cc, 0xd9ed, 0xe98e, 0xf9af, 0x8948, 0x9969,
    0xa90a, 0xb92b, 0x5af5, 0x4ad4, 0x7ab7, 0x6a96,
    0x1a71, 0x0a50, 0x3a33, 0x2a12, 0xdbfd, 0xcbdc,
    0xfbbf, 0xeb9e, 0x9b79, 0x8b58, 0xbb3b, 0xab1a,
    0x6ca6, 0x7c87, 0x4ce4, 0x5cc5, 0x2c22, 0x3c03,
    0x0c60, 0x1c41, 0xedae, 0xfd8f, 0xcdec, 0xddcd,
    0xad2a, 0xbd0b, 0x8d68, 0x9d49, 0x7e97, 0x6eb6,
    0x5ed5, 0x4ef4, 0x3e13, 0x2e32, 0x1e51, 0x0e70,
    0xff9f, 0xefbe, 0xdfdd, 0xcffc, 0xbf1b, 0xaf3a,
    0x9f59, 0x8f78, 0x9188, 0x81a9, 0xb1ca, 0xa1eb,
    0xd10c, 0xc12d, 0xf14e, 0xe16f, 0x1080, 0x00a1,
    0x30c2, 0x20e3, 0x5004, 0x4025, 0x7046, 0x6067,
    0x83b9, 0x9398, 0xa3fb, 0xb3da, 0xc33d, 0xd31c,
    0xe37f, 0xf35e, 0x02b1, 0x1290, 0x22f3, 0x32d2,
    0x4235, 0x5214, 0x6277, 0x7256, 0xb5ea, 0xa5cb,
    0x95a8, 0x8589, 0xf56e, 0xe54f, 0xd52c, 0xc50d,
    0x34e2, 0x24c3, 0x14a0, 0x0481, 0x7466, 0x6447,
    0x5424, 0x4405, 0xa7db, 0xb7fa, 0x8799, 0x97b8,
    0xe75f, 0xf77e, 0xc71d, 0xd73c, 0x26d3, 0x36f2,
    0x0691, 0x16b0, 0x6657, 0x7676, 0x4615, 0x5634,
    0xd94c, 0xc96d, 0xf90e, 0xe92f, 0x99c8, 0x89e9,
    0xb98a, 0xa9ab, 0x5844, 0x4865, 0x7806, 0x6827,
    0x18c0, 0x08e1, 0x3882, 0x28a3, 0xcb7d, 0xdb5c,
    0xeb3f, 0xfb1e, 0x8bf9, 0x9bd8, 0xabbb, 0xbb9a,
    0x4a75, 0x5a54, 0x6a37, 0x7a16, 0x0af1, 0x1ad0,
    0x2ab3, 0x3a92, 0xfd2e, 0xed0f, 0xdd6c, 0xcd4d,
    0xbdaa, 0xad8b, 0x9de8, 0x8dc9, 0x7c26, 0x6c07,
    0x5c64, 0x4c45, 0x3ca2, 0x2c83, 0x1ce0, 0x0cc1,
    0xef1f, 0xff3e, 0xcf5d, 0xdf7c, 0xaf9b, 0xbfba,
    0x8fd9, 0x9ff8, 0x6e17, 0x7e36, 0x4e55, 0x5e74,
    0x2e93, 0x3eb2, 0x0ed1, 0x1ef0
  ];

  const calculateCRC16 = (str) => {
    let crc = 0xFFFF;
    for (let i = 0; i < str.length; i++) {
      const c = str.charCodeAt(i);
      if (c > 255) throw new RangeError("Invalid character in string");
      const j = (c ^ (crc >> 8)) & 0xFF;
      crc = crcTable[j] ^ (crc << 8);
    }
    return ((crc ^ 0) & 0xFFFF).toString(16).toUpperCase().padStart(4, '0');
  };

  const padLeft = (str, n) => str.toString().padStart(n, '0');

  const generatePayNowQRData = (amount, reference) => {
    const amountStr = parseFloat(amount).toFixed(2);
    
    const payload = [
      { id: '00', value: '01' },
      { id: '01', value: '12' },
      {
        id: '26', value: [
          { id: '00', value: 'SG.PAYNOW' },
          { id: '01', value: '3' },
          { id: '02', value: 'UEN201422384R#WISE' },
          { id: '03', value: '1' }
        ]
      },
      { id: '52', value: '0000' },
      { id: '53', value: '702' },
      { id: '54', value: amountStr },
      { id: '58', value: 'SG' },
      { id: '59', value: 'FOOBALL AND MORE' },
      { id: '60', value: 'Singapore' },
      {
        id: '62', value: [
          // Tag 62/01 is the PayNow proxy/account-routing field. It MUST be
          // the fixed Wise account identifier "A21882030QR" — this is the
          // value Wise uses to route the inbound PayNow transfer into the
          // academy's account. Encoding the per-invoice SCC-XXX reference
          // here causes Wise to reject the payment and return funds to
          // the sender after 1 day. The SCC reference must only appear as
          // human-readable display text alongside the QR.
          { id: '01', value: 'A21882030QR' }
        ]
      }
    ];

    const buildTLV = (items) => {
      return items.map(item => {
        let value;
        if (Array.isArray(item.value)) {
          value = buildTLV(item.value);
        } else {
          value = item.value;
        }
        return `${item.id}${padLeft(value.length, 2)}${value}`;
      }).join('');
    };

    const dataWithoutCRC = buildTLV(payload) + '6304';
    const crc = calculateCRC16(dataWithoutCRC);
    return dataWithoutCRC + crc;
  };

  const copyToClipboard = (text, fieldId) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldId);
    setTimeout(() => setCopiedField(null), 2000);
    toast.success("Copied to clipboard!");
  };

  // Fetch terms on load
  useEffect(() => {
    fetchTerms();
  }, []);

  // Fetch sessions and rate table when term changes
  useEffect(() => {
    if (selectedTermId) {
      fetchSessions(selectedTermId);
      const term = terms.find(t => t.id === selectedTermId);
      setSelectedTerm(term);
      // Prefer the term's frozen rate_table_snapshot — that's exactly what the
      // BACKEND prices from, so the on-screen quote always matches the invoice.
      // Finalized terms often carry only a snapshot (no resolvable
      // rate_table_id); without this we'd silently fall back to the raw
      // session.price (e.g. $47 instead of a max-capacity $45). Fall back to
      // fetching by id only when there is no snapshot.
      if (term?.rate_table_snapshot) {
        setRateTable(term.rate_table_snapshot);
      } else if (term?.rate_table_id) {
        fetchRateTable(term.rate_table_id);
      } else {
        setRateTable(null);
      }
    } else {
      setSessions([]);
      setSelectedTerm(null);
      setRateTable(null);
    }
  }, [selectedTermId, terms]);

  // Calculate fee when children sessions change
  useEffect(() => {
    calculateFee();
  }, [children, selectedTerm, sessions, isSccMember, membershipValid, rateTable, purchaseDigitalMembership, parentStoredVerified, parentAlreadyHasDM, kitExtrasTotal]);

  // Jun 2026 — is the entered number a family-membership number? (ends in a
  // valid family suffix). Drives the "Main SCC Member's last name" field.
  const FAMILY_SUFFIXES = ["HFM", "WFM", "FFM", "MFM"];
  const isFamilyMembership = FAMILY_SUFFIXES.some(
    (s) => (membershipNumber || "").trim().toUpperCase().endsWith(s)
  );

  // The single answer to "is this a club member or a digital member, and what
  // does it do to the price". Same inputs and stacking rules as calculateFee()
  // above, so the note can never disagree with the quote.
  const membershipStatus = useMemo(() => {
    const md = rateTable?.membership_discounts || {};
    const clubV = Number(md?.club_member?.value || 0);
    const dmV = Number(md?.digital_member?.value || 0);
    const stackingMode = rateTable?.discount_stacking_mode || "highest_only";
    const typed = (membershipNumber || "").trim();
    const clubOk = (isSccMember && membershipValid === true) || parentStoredVerified;
    const dmActive = !!parentAlreadyHasDM
      || (!!purchaseDigitalMembership && !!rateTable?.digital_membership_config?.enabled);

    const stacks = stackingMode === "club_digital_stack" || stackingMode === "full_stack";
    let percent = 0;
    if (stacks) {
      if (clubOk) percent += clubV;
      if (dmActive) percent += dmV;
    } else {
      if (clubOk) percent = Math.max(percent, clubV);
      if (dmActive) percent = Math.max(percent, dmV);
    }

    let label = "No member discount";
    if (clubOk && dmActive) label = stacks ? "Club Member + Digital Member" : "Club Member";
    else if (clubOk) label = "Club Member";
    else if (dmActive) label = "Digital Member";

    const reasons = [];
    if (clubOk) {
      reasons.push(parentStoredVerified
        ? "Verified as an SCC club member in Member Management — revoke it there if they should not get this"
        : `Club membership number ${typed.toUpperCase()} matches the SCC format`);
    }
    if (dmActive) {
      reasons.push(parentAlreadyHasDM
        ? "Holds an Academy Digital Membership — counts only if it is still valid on the term's start date"
        : "Digital Membership is being bought on this registration");
    }
    if (clubOk && dmActive && !stacks) {
      reasons.push("They hold both; only the higher of the two applies, so the club rate is the one used");
    }
    if (isSccMember && typed && membershipValid === false) {
      reasons.push(clubOk
        ? `${typed.toUpperCase()} is not an SCC club number — the discount is coming from their verification, not this number`
        : `${typed.toUpperCase()} is not an SCC club number. ${membershipError} You can still register at full price and refund the difference once the club confirms.`);
    }
    if (!clubOk && !dmActive && !typed && isSccMember) {
      reasons.push("Ticking the box alone does not grant it — add the club number or verify them first");
    }
    return { percent, label, reasons, pending: !rateTable && (clubOk || dmActive), entitled: clubOk || dmActive };
  }, [rateTable, membershipNumber, membershipValid, membershipError, isSccMember,
      parentStoredVerified, parentAlreadyHasDM, purchaseDigitalMembership]);

  // Validate the SCC membership-number format (debounced). The club member
  // discount is applied ONLY when this returns valid. Mirrors the backend
  // gate so the on-screen quote always matches the invoice.
  useEffect(() => {
    if (!isSccMember || !(membershipNumber || "").trim()) {
      setMembershipValid(null);
      setMembershipError("");
      setMembershipValidating(false);
      return;
    }
    const num = (membershipNumber || "").trim().toUpperCase();
    const familySuffix = FAMILY_SUFFIXES.some((s) => num.endsWith(s));
    // For a family number we need the primary member's last name first.
    if (familySuffix && !(primaryMemberLastName || "").trim()) {
      setMembershipValidating(false);
      setMembershipValid(false);
      setMembershipError("Enter the Main SCC Member's last name to validate this family membership.");
      return;
    }
    let cancelled = false;
    setMembershipValidating(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/academy/validate-membership-format`, {
          membership_number: membershipNumber,
          last_name: parentLastName,
          primary_member_last_name: primaryMemberLastName,
          check_family_surname: true,
        });
        if (cancelled) return;
        setMembershipValid(!!res.data?.valid);
        setMembershipError(res.data?.valid ? "" : (res.data?.error || "This number does not match the SCC membership format."));
      } catch (e) {
        if (cancelled) return;
        // Fail-closed for pricing: do NOT grant the discount if we can't verify.
        setMembershipValid(false);
        setMembershipError("Could not validate the membership number. Please re-check it.");
      } finally {
        if (!cancelled) setMembershipValidating(false);
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [isSccMember, membershipNumber, primaryMemberLastName, parentLastName]);

  const fetchTerms = async () => {
    try {
      const response = await axios.get(`${API}/academy/terms`);
      const availableTerms = response.data.filter(t => t.is_finalized);
      setTerms(availableTerms);
    } catch (error) {
      console.error("Error fetching terms:", error);
      toast.error("Failed to load terms");
    }
  };

  const fetchSessions = async (termId) => {
    try {
      const response = await axios.get(`${API}/academy/terms/${termId}/sessions`);
      setSessions(response.data);
    } catch (error) {
      console.error("Error fetching sessions:", error);
      toast.error("Failed to load sessions");
    }
  };

  const fetchRateTable = async (rateTableId) => {
    try {
      const response = await axios.get(`${API}/academy/rate-tables`);
      const table = response.data.find(t => t.id === rateTableId);
      setRateTable(table);
    } catch (error) {
      console.error("Error fetching rate table:", error);
    }
  };

  // Email lookup function
  const handleEmailLookup = async () => {
    if (!lookupEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    setLookupLoading(true);
    try {
      const response = await axios.post(`${API}/academy/admin/lookup-user`, {
        email: lookupEmail.trim()
      });

      setLookupResult(response.data);

      if (response.data.found) {
        toast.success(`Found user: ${response.data.user.given_name} ${response.data.user.last_name}`);
      } else {
        toast.info("No existing user found. You can proceed with new registration.");
        // Set email for new registration
        setParentEmail(lookupEmail.trim());
      }
    } catch (error) {
      console.error("Error looking up user:", error);
      { const __e = formatAdminError(error, "Failed to lookup user"); toast.error(__e.message, __e.opts); };
    } finally {
      setLookupLoading(false);
    }
  };

  // Load user details into form
  const handleLoadUserDetails = () => {
    if (!lookupResult?.found || !lookupResult?.user) return;

    const user = lookupResult.user;
    setParentGivenName(user.given_name || "");
    setParentLastName(user.last_name || "");
    setParentEmail(user.email || "");
    setParentPhone(user.phone || "");
    setParentAddress(user.address || "");
    setParentNationality(user.nationality || "");
    setIsSccMember(user.is_scc_member || false);
    {
      const stored = (user.membership_number || "").trim();
      const portalIssued = /^SCC-?[0-9A-F]{6,}$/i.test(stored) || /^CT/i.test(stored)
        || /^T\d{3,4}$/i.test(stored) || /^SCC\d+$/i.test(stored);
      setParentPortalRef(portalIssued ? stored : "");
      setMembershipNumber(portalIssued ? "" : stored);
    }
    setExistingUserId(user.id);
    // Feb 2026 fix — flag if parent already has an active DM, so the UI
    // can disable the toggle and the backend skips the double-charge.
    setParentAlreadyHasDM(!!user.is_digital_member);
    setParentStoredVerified(!!user.scc_member_verified);
    if (user.is_digital_member) setPurchaseDigitalMembership(false);

    toast.success("User details loaded successfully!");

    // Show existing children if any
    if (lookupResult.existing_children?.length > 0) {
      setShowExistingChildren(true);
    }
  };

  // Add existing child to registration
  const handleAddExistingChild = (existingChild) => {
    const newChild = {
      ...INITIAL_CHILD,
      given_name: existingChild.given_name || "",
      last_name: existingChild.last_name || "",
      birthdate: existingChild.birthdate || "",
      gender: existingChild.gender || "male",
      nationality: existingChild.nationality || "",
      school: existingChild.school || "",
      email: existingChild.email || "",
      phone: existingChild.phone || "",
      medical_conditions: existingChild.medical_conditions || "",
      emergency_contact_name: existingChild.emergency_contact_name || `${parentGivenName} ${parentLastName}`.trim(),
      emergency_contact: existingChild.emergency_contact || parentPhone,
      emergency_relationship: existingChild.emergency_relationship || "Parent",
      is_existing: true,
      existing_id: existingChild.id,
      membership_number: existingChild.membership_number,
      // Waived only when the child actually PAID an earlier registration — a
      // player record alone is created as soon as a registration is started.
      charge_registration_fee: existingChild.registration_fee_paid !== true,
    };

    // Check if already added
    const alreadyAdded = children.some(c => 
      c.is_existing && c.existing_id === existingChild.id
    );

    if (alreadyAdded) {
      toast.error("This child is already added to the registration");
      return;
    }

    // If first child is empty, replace it
    if (children.length === 1 && !children[0].given_name && !children[0].last_name) {
      setChildren([newChild]);
    } else {
      setChildren([...children, newChild]);
    }

    toast.success(`Added ${existingChild.given_name} to registration`);
  };

  // Check for duplicate child
  const checkDuplicateChild = async (childIndex, givenName, lastName, birthdate) => {
    if (!givenName || !lastName || !birthdate) return;

    try {
      const response = await axios.post(`${API}/academy/admin/check-duplicate-child`, {
        given_name: givenName,
        last_name: lastName,
        birthdate: birthdate
      });

      if (response.data.is_duplicate) {
        setDuplicateWarnings(prev => ({
          ...prev,
          [childIndex]: response.data
        }));
      } else {
        setDuplicateWarnings(prev => {
          const newWarnings = { ...prev };
          delete newWarnings[childIndex];
          return newWarnings;
        });
      }
    } catch (error) {
      console.error("Error checking duplicate:", error);
    }
  };

  const roundTo2 = (num) => Math.round(num * 100) / 100;
  // Display a per-session rate at just enough precision to reconcile with the
  // charged line total: 2dp when it's exact, otherwise up to 4dp (trimmed).
  const fmtRate = (n) => {
    const v = Number(n) || 0;
    const r2 = Math.round(v * 100) / 100;
    return Math.abs(r2 - v) < 0.00005 ? r2.toFixed(2) : v.toFixed(4).replace(/0+$/, "");
  };

  const calculateFee = () => {
    if (!selectedTerm || sessions.length === 0) {
      setCalculatedFee(0);
      setFeeBreakdown(null);
      return;
    }

    const termWeeks = selectedTerm.duration_unit === "weeks" 
      ? (selectedTerm.duration_value || 10)
      : (selectedTerm.training_weeks || 10);

    const enrolledChildren = children.filter(c => c.session_ids.length > 0);
    const enrolledChildrenCount = enrolledChildren.length;

    // Jun 2026 — single session dates cancelled by the academy are NOT billed
    // (only on the weekday they fall on). Mirrors the backend recalc so the
    // admin quote matches the invoice, and names the dates for the breakdown.
    const cancelledTermDates = (selectedTerm?.cancelled_dates || [])
      .map((c) => (typeof c === "string" ? c : c?.date))
      .filter(Boolean)
      .map((d) => new Date(`${String(d).slice(0, 10)}T00:00:00`))
      .filter((d) => !isNaN(d));
    const isoDow = (d) => (d.getDay() === 0 ? 7 : d.getDay());
    const cancelledDatesForDay = (dow) =>
      cancelledTermDates.filter((d) => isoDow(d) === Number(dow));
    const cancellationNotes = [];
    let totalCancellationOffset = 0;

    // Feb 2026 fix — sibling discount must follow the same per-child rule
    // the backend enforces: child #1 pays full; child #2 gets the
    // discount tier with `child_number === 2`; child #3 gets the
    // child_number === 3 tier (or 2 if no #3 tier defined). Picking ONE
    // tier based on total kid count and applying it uniformly produced
    // a lower total in the admin UI than what the backend actually
    // invoiced — a direct revenue leak. Sorted ASC so the lookup-by-
    // position picks the highest applicable tier.
    const siblingTiersAsc = (rateTable?.sibling_discounts_enabled && rateTable?.sibling_discounts)
      ? [...rateTable.sibling_discounts].sort((a, b) => a.child_number - b.child_number)
      : [];

    const siblingPctForPosition = (enrollPosition) => {
      // 1-based enrollment index. Position 1 NEVER gets a discount.
      if (enrollPosition < 2 || siblingTiersAsc.length === 0) return 0;
      let pct = 0;
      for (const t of siblingTiersAsc) {
        if (enrollPosition >= t.child_number && t.type === "percentage") {
          pct = t.value;  // accumulate the highest applicable
        }
      }
      return pct;
    };

    // Legacy uniform pct used ONLY for the sibling-discount summary line
    // in the breakdown panel. The actual per-child math uses
    // `siblingPctForPosition` so positions 1, 2, 3 each get the right tier.
    const siblingDiscountPercent = enrolledChildrenCount > 1
      ? siblingPctForPosition(enrolledChildrenCount)
      : 0;

    let memberDiscountPercent = 0;
    // Feb 2026 fix — apply membership discount per backend's
    // `discount_stacking_mode` semantics (mirrors academy_routes.py:7039–7092).
    // Previously this only read `club_member` and ignored DM entirely, which
    // under-quoted the per-child rate whenever the admin checked "Purchase
    // Digital Membership" — leaving the admin to over-quote by ~10% × eldest
    // child fees vs what the backend would actually invoice.
    {
      const md = rateTable?.membership_discounts || {};
      const clubV = Number(md?.club_member?.value || 0);
      const dmV = Number(md?.digital_member?.value || 0);
      const sectionV = Number(md?.section_member?.value || 0);
      const stackingMode = rateTable?.discount_stacking_mode || "highest_only";
      // For admin-register-for-parent we don't currently capture section
      // membership separately, so treat "isSccMember" as club-only. DM
      // discount applies whenever the parent will be a digital member at
      // payment time — either they already are one, or they're purchasing
      // DM through this flow. The `enabled` flag only gates whether THIS
      // term can sell DM (used for the purchase-side check); a parent who
      // already holds DM still gets the discount regardless.
      const canPurchaseDmHere = !!rateTable?.digital_membership_config?.enabled;
      const willBeDmAtPayment = !!parentAlreadyHasDM || (!!purchaseDigitalMembership && canPurchaseDmHere);
      const dmActive = willBeDmAtPayment;

      // Jun 2026 — the SCC CLUB member discount is granted ONLY when the
      // entered membership number passes format validation. Ticking the
      // "SCC Member" checkbox alone is NOT enough (mirrors the backend, which
      // withholds the discount until the parent is verified). Digital
      // Membership is independent and NOT gated on the SCC number.
      const sccMemberValid = (isSccMember && membershipValid === true) || parentStoredVerified;

      if (stackingMode === "highest_only") {
        let pct = 0;
        if (sccMemberValid && clubV > pct) pct = clubV;
        if (dmActive && dmV > pct) pct = dmV;
        // Section ignored here (no UI capture); falls through unchanged.
        memberDiscountPercent = pct;
      } else if (stackingMode === "club_digital_stack") {
        // Club + DM stack; section is standalone (not captured here).
        let pct = 0;
        if (sccMemberValid && clubV > 0) pct += clubV;
        if (dmActive && dmV > 0) pct += dmV;
        memberDiscountPercent = pct;
      } else if (stackingMode === "full_stack") {
        let pct = 0;
        if (sccMemberValid && clubV > 0) pct += clubV;
        if (dmActive && dmV > 0) pct += dmV;
        if (sectionV > 0) pct += sectionV;
        memberDiscountPercent = pct;
      } else {
        // Unknown mode → safe default = highest_only behaviour
        let pct = 0;
        if (sccMemberValid && clubV > pct) pct = clubV;
        if (dmActive && dmV > pct) pct = dmV;
        memberDiscountPercent = pct;
      }
    }

    const pricingMode = rateTable?.pricing_mode || "progressive";
    const volumeTiers = rateTable?.volume_pricing_tiers || [];

    let totalBaseFee = 0;
    let totalOriginalFee = 0;
    const childBreakdowns = [];

    let enrollIdx = 0;
    children.forEach((child, childIndex) => {
      if (child.session_ids.length === 0) return;
      enrollIdx += 1;
      // Resolve THIS child's sibling tier (0 for kid #1; tier for #2/#3/...).
      const childSiblingPct = siblingPctForPosition(enrollIdx);

      // Only count sessions that resolve to a real session template. A
      // re-enrolling child can carry stale/duplicate session_ids that no longer
      // map to a live session; counting those inflated the volume-tier lookup
      // (e.g. 4-session $35.50 rate when only 2 sessions were actually selected,
      // which then billed 2 × $35.50 instead of the correct 2 × $41.83). Use
      // the resolved count everywhere so tier + count + billed lines agree.
      const resolvedSessionIds = child.session_ids.filter(sid => sessions.some(s => s.id === sid));
      const sessionCount = resolvedSessionIds.length || child.session_ids.length;
      let childWeeklyFee = 0;
      let childOriginalWeeklyFee = 0;
      let pricePerSession = 0;
      let originalPricePerSession = 0;

      const isMaxCapacity = pricingMode === "max_capacity";
      const mcPrice = parseFloat(rateTable?.max_capacity_price_per_session) || 0;
      const mcMaxSessions = parseInt(rateTable?.max_sessions_per_week) || 0;

      if (isMaxCapacity && mcPrice > 0 && mcMaxSessions > 0) {
        // FIXED TERM (MAX CAPACITY) — Jun 2026. Parent is ALWAYS billed for
        // max_sessions_per_week × term_weeks × the rate-table price, no matter
        // how many sessions are picked. The per-session price comes from the
        // RATE TABLE (`max_capacity_price_per_session`), never the individual
        // session record's `price` (which can be stale, e.g. a copied $47).
        // This mirrors the backend canonical engine (academy_routes.py:9085)
        // and the parent wizard (ParentRegistrationWizard.js:1255).
        // childTermFee is computed GROSS here (no member/sibling). Both
        // discounts are applied ONCE at the end on the un-rounded gross so the
        // quote + Scan-to-Pay QR match the backend invoice to the cent (the
        // backend rounds only the final total; rounding a sibling-adjusted
        // per-session price mid-way lost ~7c on multi-child volume terms).
        originalPricePerSession = mcPrice;
        pricePerSession = mcPrice;
        childWeeklyFee = roundTo2(mcPrice * mcMaxSessions);
        childOriginalWeeklyFee = roundTo2(mcPrice * mcMaxSessions);
      } else if (pricingMode === "volume" && volumeTiers.length > 0) {
        const sortedTiers = [...volumeTiers].sort((a, b) => b.min_sessions - a.min_sessions);
        const applicableTier = sortedTiers.find(t => sessionCount >= t.min_sessions);
        // Keep the tier price UN-rounded for the money calc (round only the
        // final child total below).
        pricePerSession = applicableTier?.price_per_session || 0;

        const singleTier = volumeTiers.find(t => t.min_sessions === 1);
        originalPricePerSession = singleTier?.price_per_session || pricePerSession;

        childWeeklyFee = roundTo2(pricePerSession * sessionCount);
        childOriginalWeeklyFee = roundTo2(originalPricePerSession * sessionCount);
      } else {
        child.session_ids.forEach(sessionId => {
          const session = sessions.find(s => s.id === sessionId);
          if (session) {
            const sessionPrice = session.price || 0;
            childWeeklyFee += sessionPrice;
            childOriginalWeeklyFee += sessionPrice;
          }
        });
        pricePerSession = childWeeklyFee / sessionCount;
        originalPricePerSession = childOriginalWeeklyFee / sessionCount;
      }

      // Sprint 1 v2: per-session billed counts. If the override map is
      // populated, sum each session's individual billed count × price.
      // Else fall back to the legacy uniform behaviour (× termWeeks).
      const overrideMap = child.session_count_overrides || {};
      const hasPerSession = Object.keys(overrideMap).length > 0;
      let childTermFee = 0;
      let childOriginalTermFee = 0;
      let mcBilledWeeks = termWeeks;
      if (isMaxCapacity && mcPrice > 0 && mcMaxSessions > 0) {
        // Fixed term: fee = weekly package (max sessions/week × price) × the
        // number of WEEKS billed. Admins CAN reduce the weeks (e.g. a late
        // joiner) via the "Adjust billable sessions per line" panel; for a
        // fixed weekly package we treat the billed value as weeks (uniform —
        // use the max if lines differ, matching the backend recalc).
        const ovVals = Object.values(overrideMap)
          .map((v) => parseInt(v, 10))
          .filter((v) => !isNaN(v));
        mcBilledWeeks = ovVals.length ? Math.max(...ovVals) : termWeeks;
        childTermFee = roundTo2(childWeeklyFee * mcBilledWeeks);
        childOriginalTermFee = roundTo2(childOriginalWeeklyFee * mcBilledWeeks);
      } else if (hasPerSession) {
        for (const sid of child.session_ids) {
          const sess = sessions.find((s) => s.id === sid);
          if (!sess) continue;
          const billed = parseInt(overrideMap[sid] ?? termWeeks, 10) || termWeeks;
          // Effective price (after volume tier) is captured below as
          // pricePerSession; original is the listed price.
          childTermFee = roundTo2(childTermFee + pricePerSession * billed);
          childOriginalTermFee = roundTo2(childOriginalTermFee + originalPricePerSession * billed);
        }
      } else {
        childTermFee = roundTo2(childWeeklyFee * termWeeks);
        childOriginalTermFee = roundTo2(childOriginalWeeklyFee * termWeeks);
      }
      // Cancelled single session dates → remove those occurrences from the
      // billed weeks of their own weekday, before any discount.
      let childCancelOffset = 0;
      for (const sid of child.session_ids) {
        const sess = sessions.find((s) => s.id === sid);
        if (!sess) continue;
        const hits = cancelledDatesForDay(sess.day_of_week ?? sess.day);
        if (hits.length === 0) continue;
        const rate = (isMaxCapacity && mcPrice > 0)
          ? mcPrice
          : (pricingMode === "volume" ? pricePerSession : (sess.price || 0));
        childCancelOffset += rate * hits.length;
        hits.forEach((h) => {
          const label = h.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short" });
          if (!cancellationNotes.includes(label)) cancellationNotes.push(label);
        });
      }
      childCancelOffset = roundTo2(childCancelOffset);
      if (childCancelOffset > 0) {
        childTermFee = roundTo2(Math.max(0, childTermFee - childCancelOffset));
        childOriginalTermFee = roundTo2(Math.max(0, childOriginalTermFee - childCancelOffset));
        totalCancellationOffset = roundTo2(totalCancellationOffset + childCancelOffset);
      }

      // childTermFee is now the GROSS term fee (no discounts). Apply BOTH
      // discounts on the un-rounded gross and round ONCE — membership first,
      // then sibling — mirroring the backend engine exactly (prevents the
      // ~7c drift that made the QR under-state the invoice).
      const memFrac = memberDiscountPercent / 100;
      const sibFrac = childSiblingPct / 100;
      const childGross = childTermFee;
      const memberDiscount = roundTo2(childGross * memFrac);
      const siblingDiscount = roundTo2(childGross * (1 - memFrac) * sibFrac);
      const childFinalFee = roundTo2(childGross * (1 - memFrac) * (1 - sibFrac));

      const effectivePricePerSession = roundTo2(
        pricePerSession * (1 - memFrac) * (1 - sibFrac)
      );

      // Aggregate for review-card single-line summary (only meaningful when
      // every line has the same billed count; else show "varies").
      const billedValues = hasPerSession
        ? child.session_ids.map((sid) => parseInt(overrideMap[sid] ?? termWeeks, 10) || termWeeks)
        : [termWeeks];
      const allSame = billedValues.every((v) => v === billedValues[0]);

      childBreakdowns.push({
        childIndex,
        name: `${child.given_name} ${child.last_name}`.trim() || `Child ${childIndex + 1}`,
        sessionCount,
        pricePerSession: effectivePricePerSession,
        pricePerSessionExact: pricePerSession * (1 - memFrac) * (1 - sibFrac),
        originalPricePerSession: roundTo2(originalPricePerSession),
        volumePricePerSession: roundTo2(pricePerSession),
        weeklyFee: childWeeklyFee,
        termFee: childTermFee,
        cancellationOffset: childCancelOffset,
        originalTermFee: childOriginalTermFee,
        memberDiscount,
        memberDiscountPercent,
        siblingDiscount,
        siblingDiscountPercent: childSiblingPct,
        finalFee: childFinalFee,
        // Sprint 1 v2: per-session breakdown for the review card
        plannedWeeks: (isMaxCapacity && mcPrice > 0 && mcMaxSessions > 0) ? mcBilledWeeks : termWeeks,
        billedWeeks: allSame ? billedValues[0] : null,
        billedSummary: allSame ? `${billedValues[0]}` : `${Math.min(...billedValues)}–${Math.max(...billedValues)} (varies)`,
        hasPerSessionOverride: hasPerSession,
        sessionCountOverride: hasPerSession ? null : (child.session_count_override ?? null),
        sessionCountOverrideReason: child.session_count_override_reason || "",
        // Fixed Term (Max Capacity): parent is billed for max_sessions_per_week
        // regardless of how many are picked — so the breakdown line must show
        // the billed rate (max), not the picked count.
        isMaxCapacity: isMaxCapacity && mcPrice > 0 && mcMaxSessions > 0,
        billedSessionsPerWeek: (isMaxCapacity && mcMaxSessions > 0) ? mcMaxSessions : sessionCount
      });

      totalBaseFee += childFinalFee;
      totalOriginalFee += childOriginalTermFee;
    });

    // Jun 2026 fix — sum the TRUE per-child sibling discounts (membership-first
    // convention) so the summary line matches the invoice. The old formula
    // (totalOriginal − totalBase − memberDiscount) conflated the volume-price
    // gap with the sibling saving and produced an inflated, misleading figure.
    const siblingDiscountSavings = roundTo2(
      childBreakdowns.reduce((sum, c) => sum + (c.siblingDiscount || 0), 0)
    );

    // Feb 2026 fix — surface Sign-up Registration Fee per child + parent
    // Digital Membership in the calculated total (admin had no way to add
    // them before, so neither was making it onto invoices).
    const regFeePerChild = rateTable?.registration_fee?.enabled
      ? Number(rateTable.registration_fee.amount || 0)
      : 0;
    const regFeeTotal = regFeePerChild > 0
      ? children.filter(c => c.charge_registration_fee && c.session_ids.length > 0)
                .length * regFeePerChild
      : 0;
    // DM price lookup: rate tables store the canonical 1-year price under
    // `digital_membership_config.annual_price`. The legacy `.price` field
    // is only present on one production rate table, so reading it first
    // caused every other term to show "rate table not configured for DM"
    // even when DM was enabled. Fix: read annual_price first, fall back
    // to price for backwards compat.
    const dmCfg = rateTable?.digital_membership_config || {};
    const dmConfiguredPrice = Number(dmCfg.annual_price ?? dmCfg.price ?? 0);
    const dmPrice = (purchaseDigitalMembership && !parentAlreadyHasDM && dmCfg.enabled)
      ? dmConfiguredPrice
      : 0;
    const grandTotal = roundTo2(totalBaseFee + regFeeTotal + dmPrice);

    setFeeBreakdown({
      childBreakdowns,
      termWeeks,
      enrolledChildrenCount,
      siblingDiscountPercent,
      siblingDiscountSavings,
      memberDiscountPercent,
      totalBaseFee,
      totalOriginalFee,
      cancellationOffsetTotal: totalCancellationOffset,
      cancellationNotes,
      pricingMode,
      // Feb 2026 fix breakdown lines
      regFeePerChild,
      regFeeTotal,
      dmPrice,
      grandTotal,
    });

    setCalculatedFee(grandTotal + kitExtrasTotal);
  };

  const addChild = () => {
    setChildren([...children, { 
      ...INITIAL_CHILD,
      emergency_contact_name: `${parentGivenName} ${parentLastName}`.trim(),
      emergency_contact: parentPhone
    }]);
  };

  const removeChild = (index) => {
    if (children.length > 1) {
      setChildren(children.filter((_, i) => i !== index));
      setDuplicateWarnings(prev => {
        const newWarnings = { ...prev };
        delete newWarnings[index];
        return newWarnings;
      });
    }
  };

  const updateChild = (index, field, value) => {
    const updated = [...children];
    updated[index] = { ...updated[index], [field]: value };
    setChildren(updated);

    // Check for duplicates when name/birthdate changes
    if (["given_name", "last_name", "birthdate"].includes(field)) {
      const child = updated[index];
      checkDuplicateChild(index, child.given_name, child.last_name, child.birthdate);
    }
  };

  const toggleSessionForChild = (childIndex, sessionId) => {
    const updated = [...children];
    const currentSessions = updated[childIndex].session_ids;
    
    if (currentSessions.includes(sessionId)) {
      updated[childIndex].session_ids = currentSessions.filter(id => id !== sessionId);
    } else {
      updated[childIndex].session_ids = [...currentSessions, sessionId];
    }
    
    setChildren(updated);
  };

  const getDayName = (dayNumber) => {
    const days = ["", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    return days[dayNumber] || `Day ${dayNumber}`;
  };

  const calculateAge = (birthdate) => {
    if (!birthdate) return null;
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Validation before review
  const validateForm = () => {
    if (!parentGivenName || !parentLastName || !parentEmail || !parentPhone) {
      toast.error("Please fill in all required parent fields");
      return false;
    }

    if (!selectedTermId) {
      toast.error("Please select a term");
      return false;
    }

    const validChildren = children.filter(c => c.given_name && c.last_name && c.birthdate);
    if (validChildren.length === 0) {
      toast.error("Please add at least one child with name and birthdate");
      return false;
    }

    const childrenWithSessions = validChildren.filter(c => c.session_ids.length > 0);
    if (childrenWithSessions.length === 0) {
      toast.error("Please select at least one session for at least one child");
      return false;
    }

    // Sprint 1 v2 (May 2026) — per-session override map. If any session
    // line for any child has been set to a value different from termWeeks,
    // a single shared reason per child is mandatory (carried onto invoice).
    // NOTE: termWeeks MUST be resolved here — it is not in this function's
    // outer scope. Omitting it made the override `.some()` callback throw a
    // ReferenceError, which silently aborted the Review button whenever an
    // adjusted line existed (registration could proceed only with no override).
    const selectedTerm = terms.find(t => t.id === selectedTermId);
    const termWeeks = selectedTerm
      ? (selectedTerm.duration_unit === "weeks"
          ? (selectedTerm.duration_value || 10)
          : (selectedTerm.training_weeks || 10))
      : 10;
    for (const c of childrenWithSessions) {
      const map = c.session_count_overrides || {};
      const anyAdjusted = Object.entries(map).some(([_sid, billed]) => {
        const v = parseInt(billed, 10);
        return Number.isFinite(v) && v >= 1 && v !== termWeeks;
      });
      // Legacy uniform override fallback (kept for older flows).
      const uniformAdjusted = typeof c.session_count_override === "number" && c.session_count_override > 0 && c.session_count_override !== termWeeks;
      if (anyAdjusted || uniformAdjusted) {
        if (!(c.session_count_override_reason || "").trim()) {
          toast.error(
            `Please provide a reason for the session-line override on '${c.given_name || "child"}'. It will appear on the invoice.`,
            { duration: 8000 }
          );
          return false;
        }
      }
    }

    return true;
  };

  // Show review screen
  const handleShowReview = async () => {
    if (!validateForm()) return;

    // Generate payment reference for QR — includes term code for accounting
    // clarity (e.g. SCC-T1-20260503-XXXX vs SCC-T2-20260503-XXXX).
    let termCode = "";
    try {
      const selectedTerm = terms.find(t => t.id === selectedTermId);
      const m = (selectedTerm?.name || "").match(/Term\s*(\d+)/i);
      if (m) termCode = `T${m[1]}-`;
    } catch (e) {
      // best-effort — fall back to no prefix
    }
    const ref = `SCC-${termCode}${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${Math.random().toString(36).substr(2, 4).toUpperCase()}`;
    setPaymentReference(ref);

    // Generate QR code data
    const qrData = generatePayNowQRData(calculatedFee, ref);
    setQrCodeData(qrData);

    setShowReview(true);
  };

  const handleSubmit = async (overrides = false) => {
    // Defensive coercion: if handleSubmit is wired directly to an onClick
    // (onClick={handleSubmit}) the click event is passed as the first arg.
    // That event carries React fibers + DOM refs which axios.JSON.stringify
    // cannot serialize → "Converting circular structure to JSON" and the
    // request never leaves the browser. Force to a plain object.
    const opts = (overrides && typeof overrides === "object" && !overrides.nativeEvent)
      ? overrides
      : { forceReplacePending: overrides === true, skipDuplicateChildren: false };
    const forceReplacePending = !!opts.forceReplacePending;
    const skipDuplicateChildren = !!opts.skipDuplicateChildren;

    if (!validateForm()) return;

    setSubmitting(true);

    try {
      const validChildren = children.filter(c => c.given_name && c.last_name && c.birthdate);

      const payload = {
        parent_given_name: parentGivenName,
        parent_last_name: parentLastName,
        parent_email: parentEmail,
        parent_phone: parentPhone,
        parent_address: parentAddress,
        parent_nationality: parentNationality,
        is_scc_member: isSccMember,
        membership_number: membershipNumber,
        primary_member_last_name: primaryMemberLastName,
        client_quoted_total: calculatedFee,
        // Feb 2026 fix — Digital Membership for parent + per-child Sign-up
        // Registration Fee. Both were being silently dropped before.
        // DM price reads annual_price (canonical) with .price fallback.
        purchase_digital_membership: purchaseDigitalMembership && !parentAlreadyHasDM && !!rateTable?.digital_membership_config?.enabled,
        digital_membership_price: (purchaseDigitalMembership && !parentAlreadyHasDM && !!rateTable?.digital_membership_config?.enabled)
          ? Number(rateTable?.digital_membership_config?.annual_price ?? rateTable?.digital_membership_config?.price ?? 0)
          : 0,
        children: validChildren.map(c => ({
          given_name: c.given_name,
          last_name: c.last_name,
          birthdate: c.birthdate,
          gender: c.gender,
          nationality: c.nationality,
          school: c.school,
          email: c.email,
          phone: c.phone,
          medical_conditions: c.medical_conditions,
          emergency_contact_name: c.emergency_contact_name || `${parentGivenName} ${parentLastName}`.trim(),
          emergency_contact: c.emergency_contact || parentPhone,
          emergency_relationship: c.emergency_relationship || "Parent",
          session_ids: c.session_ids,
          is_existing: c.is_existing,
          existing_id: c.existing_id,
          // Feb 2026 fix — admin opts in/out per child for Sign-up Registration Fee.
          charge_registration_fee: !!c.charge_registration_fee,
          // Sprint 1 v2 (May 2026) — per-session override map.
          // Backend prefers `session_count_overrides` (dict) over the
          // legacy uniform `session_count_override` (int).
          session_count_overrides: c.session_count_overrides && Object.keys(c.session_count_overrides).length > 0
            ? c.session_count_overrides
            : null,
          session_count_override: (typeof c.session_count_override === "number" && c.session_count_override > 0)
            ? c.session_count_override
            : null,
          session_count_override_reason: ((c.session_count_overrides && Object.keys(c.session_count_overrides).length > 0)
            || (typeof c.session_count_override === "number" && c.session_count_override > 0))
            ? (c.session_count_override_reason || "").trim()
            : ""
        })),
        term_id: selectedTermId,
        payment_status: paymentStatus,
        payment_method: paymentMethod,
        payment_reference: paymentReference,
        payment_qr_reference: paymentReference,  // Save QR reference for later use
        payment_amount: parseFloat(paymentAmount) || calculatedFee,
        admin_notes: adminNotes,
        send_welcome_email: sendWelcomeEmail,
        force_replace_pending: forceReplacePending,
        skip_duplicate_children: skipDuplicateChildren,
        kit_selections: kitSelections,
      };

      const response = await axios.post(`${API}/academy/admin/register-for-parent`, payload);

      if (response.data.success) {
        // Sync the on-screen total + PayNow QR to the AUTHORITATIVE amount the
        // backend actually invoiced (response.total_fee). The QR was generated
        // at the Review step from the frontend quote (calculatedFee); if the
        // server recomputed a different figure (e.g. a Digital Membership 10%
        // the quote missed), the QR must be regenerated so the parent scans and
        // pays the SAME amount that is on the invoice.
        const authoritativeTotal = Number(response.data.total_fee);
        if (!Number.isNaN(authoritativeTotal) && authoritativeTotal > 0) {
          setCalculatedFee(authoritativeTotal);
          if (paymentReference) {
            setQrCodeData(generatePayNowQRData(authoritativeTotal, paymentReference));
          }
        }
        const qm = response.data.quote_charge_mismatch;
        if (qm) {
          toast.warning(
            `Heads up: the on-screen quote ($${Number(qm.quoted).toFixed(2)}) did not match the calculated charge ($${Number(qm.charged).toFixed(2)}). The correct amount $${Number(qm.charged).toFixed(2)} has been used on the invoice AND the PayNow QR — please review.`,
            { duration: 12000 }
          );
        } else {
          toast.success(response.data.message);
        }
        setSuccessDialog({ open: true, data: response.data });
        setShowReview(false);
      }
    } catch (error) {
      console.error("Registration error:", error);
      // P0 fix (May 2026): handle 409 "pending_registration_exists" with a
      // confirm-replace prompt so admins can recover from the Dheeraj scenario.
      const detail = error.response?.data?.detail;
      const errCode = (typeof detail === "object" ? detail.code : null);
      if (error.response?.status === 409 && errCode === "pending_registration_exists") {
        // Feb 2026 follow-up: backend now returns duplicate_child_names + new_child_names.
        // Open a 3-button modal so admin can pick Replace / Submit only new children / Cancel.
        setDuplicatePrompt(detail);
        // Hand control to the modal — submit will resume via its action handlers.
        return;
      } else {
        // Surface the underlying error so admin + logs can see it.
        // Priority: string detail → object.message → object.detail → HTTP status text → generic.
        let msg;
        if (typeof detail === "string") {
          msg = detail;
        } else if (detail?.message) {
          msg = detail.message;
        } else if (error.response?.status) {
          msg = `Registration failed (HTTP ${error.response.status}): ${error.response.statusText || "server error"}`;
        } else {
          msg = `Registration failed: ${error.message || "unknown error"}`;
        }
        toast.error(msg, { duration: 12000 });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ReviewScreen component has been inlined into the conditional rendering below

  // Duplicate/pending-registration prompt — defined once so it renders on BOTH
  // the review/QR screen and the main form. Previously it lived only in the
  // main-form return, so clicking "Complete Registration" on the review screen
  // set the state but showed NOTHING until the admin navigated back (the bug).
  const duplicatePromptDialog = (
    <Dialog
      open={!!duplicatePrompt}
      onOpenChange={(open) => { if (!open) setDuplicatePrompt(null); }}
    >
      <DialogContent
        className="bg-slate-900 border-rose-500/40 text-white max-w-lg"
        data-testid="duplicate-child-prompt"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-300">
            ⚠ Existing pending invoice
          </DialogTitle>
          <DialogDescription className="text-slate-300 pt-2">
            {duplicatePrompt?.message}
          </DialogDescription>
        </DialogHeader>
        <div className="text-xs space-y-2 bg-slate-950/60 rounded p-3 border border-slate-800">
          <div>
            <span className="text-slate-400">Pending child:</span>{" "}
            <span className="text-rose-300 font-semibold">
              {(duplicatePrompt?.duplicate_child_names || []).join(", ") || "—"}
            </span>
          </div>
          {duplicatePrompt?.new_child_names && duplicatePrompt.new_child_names.length > 0 && (
            <div>
              <span className="text-slate-400">New child{duplicatePrompt.new_child_names.length === 1 ? "" : "ren"} you're trying to add:</span>{" "}
              <span className="text-emerald-300 font-semibold">
                {duplicatePrompt.new_child_names.join(", ")}
              </span>
            </div>
          )}
          <div>
            <span className="text-slate-400">Existing invoice:</span>{" "}
            ${Number(duplicatePrompt?.amount || 0).toFixed(2)} · created {(duplicatePrompt?.created_at || "").split("T")[0]} · {duplicatePrompt?.payment_status || duplicatePrompt?.status}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 pt-3">
          <Button
            onClick={async () => {
              setDuplicatePrompt(null);
              await handleSubmit({ forceReplacePending: true });
            }}
            className="bg-rose-600 hover:bg-rose-700 text-white flex-1"
            data-testid="duplicate-replace-btn"
          >
            Replace pending invoice
          </Button>
          {duplicatePrompt?.new_child_names && duplicatePrompt.new_child_names.length > 0 && (
            <Button
              onClick={async () => {
                setDuplicatePrompt(null);
                await handleSubmit({ skipDuplicateChildren: true });
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
              data-testid="duplicate-skip-btn"
            >
              Submit only new {duplicatePrompt.new_child_names.length === 1 ? "child" : "children"}
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={() => {
              setDuplicatePrompt(null);
              toast.info("Registration cancelled. Existing pending invoice left intact.");
            }}
            className="text-slate-300 flex-1"
            data-testid="duplicate-cancel-btn"
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  // Main Form - conditionally render review screen
  if (showReview) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4 md:p-8">
        {duplicatePromptDialog}
        <div className="max-w-5xl mx-auto">
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowReview(false)}
                  className="text-slate-400 hover:text-white"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div>
                  <h1 className="text-2xl font-bold text-white">Review Registration</h1>
                  <p className="text-slate-400 text-sm">Verify details before completing registration</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Summary */}
              <div className="space-y-4">
                {/* Parent Summary */}
                <Card className="p-4 bg-slate-800/50 border-slate-700">
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-400" />
                    Parent Details
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Name:</span>
                      <span className="text-white">{parentGivenName} {parentLastName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Email:</span>
                      <span className="text-white">{parentEmail}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Phone:</span>
                      <span className="text-white">{parentPhone}</span>
                    </div>
                    {isSccMember && (
                      <div className="flex justify-between">
                        <span className="text-slate-400">Member Status:</span>
                        <span className="text-emerald-400">SCC Member</span>
                      </div>
                    )}
                  </div>
                </Card>

                {/* Children Summary */}
                <Card className="p-4 bg-slate-800/50 border-slate-700">
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <Users className="w-5 h-5 text-green-400" />
                    Children &amp; Sessions
                  </h3>
                  <div className="space-y-3">
                    {children.filter(c => c.session_ids.length > 0).map((child, idx) => (
                      <div key={idx} className="p-3 bg-slate-900/50 rounded-lg">
                        <div className="font-medium text-white mb-2">
                          {child.given_name} {child.last_name}
                          {child.is_existing && (
                            <span className="ml-2 text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">
                              Re-enrolling
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-400 space-y-1">
                          <div>DOB: {child.birthdate} (Age: {calculateAge(child.birthdate)})</div>
                          <div>{child.session_ids.length} session(s) selected</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Term & Fee Summary */}
                <Card className="p-4 bg-slate-800/50 border-slate-700">
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-amber-400" />
                    Fee Summary
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Term:</span>
                      <span className="text-white">{selectedTerm?.name}</span>
                    </div>
                    {feeBreakdown?.childBreakdowns?.map((cb, idx) => (
                      <div key={idx} className="flex justify-between">
                        <span className="text-slate-400">{cb.name}:</span>
                        <span className="text-white">${cb.finalFee.toFixed(2)}</span>
                      </div>
                    ))}
                    {feeBreakdown?.cancellationOffsetTotal > 0 && (
                      <div className="flex justify-between" data-testid="review-cancelled-session-line">
                        <span className="text-slate-400">
                          🗓️ Cancelled session not billed
                          {feeBreakdown.cancellationNotes?.length > 0
                            ? ` (${feeBreakdown.cancellationNotes.join(", ")})`
                            : ""}
                        </span>
                        <span className="text-emerald-300">-${feeBreakdown.cancellationOffsetTotal.toFixed(2)}</span>
                      </div>
                    )}
                    {feeBreakdown?.regFeeTotal > 0 && (
                      <div className="flex justify-between" data-testid="review-signup-fee-line">
                        <span className="text-slate-400">
                          📝 Sign-up Registration Fee ({Math.round(feeBreakdown.regFeeTotal / feeBreakdown.regFeePerChild)} × ${feeBreakdown.regFeePerChild.toFixed(2)})
                        </span>
                        <span className="text-white">${feeBreakdown.regFeeTotal.toFixed(2)}</span>
                      </div>
                    )}
                    {feeBreakdown?.dmPrice > 0 && (
                      <div className="flex justify-between" data-testid="review-dm-line">
                        <span className="text-slate-400">💳 Digital Membership (1 year)</span>
                        <span className="text-white">${feeBreakdown.dmPrice.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="border-t border-slate-700 pt-2 mt-2">
                      <div className="flex justify-between text-lg font-semibold">
                        <span className="text-slate-300">Total:</span>
                        <span className="text-emerald-400" data-testid="review-total-amount">${calculatedFee.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Right Column - Payment QR */}
              <div className="space-y-4">
                <Card ref={paymentQrRef} className="p-6 bg-slate-800/50 border-slate-700">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-purple-400" />
                    Scan to Pay
                  </h3>
                  
                  {/* QR Code */}
                  <div className="flex justify-center mb-4">
                    <div className="bg-white p-4 rounded-lg">
                      {qrCodeData && (
                        <QRCodeSVG
                          value={qrCodeData}
                          size={200}
                          level="M"
                          includeMargin={false}
                        />
                      )}
                    </div>
                  </div>

                  {/* Payment Details */}
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center p-2 bg-slate-900/50 rounded">
                      <span className="text-slate-400">Amount:</span>
                      <span className="text-xl font-bold text-emerald-400" data-testid="paynow-qr-amount">${calculatedFee.toFixed(2)}</span>
                    </div>
                    
                    <div className="flex justify-between items-center p-2 bg-slate-900/50 rounded">
                      <span className="text-slate-400">Invoice #:</span>
                      <div className="flex items-center gap-2">
                        <code className="text-amber-400 font-mono">{paymentReference}</code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(paymentReference, 'ref')}
                          className="h-6 w-6 p-0"
                        >
                          {copiedField === 'ref' ? (
                            <Check className="h-3 w-3 text-emerald-400" />
                          ) : (
                            <Copy className="h-3 w-3 text-slate-400" />
                          )}
                        </Button>
                      </div>
                    </div>

                    <div className="p-3 bg-red-500/10 border-2 border-red-500/40 rounded-lg">
                      <p className="text-red-300 text-xs font-bold mb-1">⚠️ DO NOT EDIT THE REFERENCE FIELD</p>
                      <p className="text-red-200 text-xs">
                        When the parent's bank app prompts to confirm the payment reference, leave it exactly as the QR has filled it. Editing will cause Wise to return the payment.
                      </p>
                    </div>
                    
                    {/* Parent Info for reference */}
                    <div className="p-2 bg-slate-900/50 rounded text-xs text-slate-400">
                      <p><strong>Parent:</strong> {parentGivenName} {parentLastName}</p>
                      <p><strong>Child:</strong> {children.map(c => `${c.given_name} ${c.last_name}`).join(', ')}</p>
                    </div>
                  </div>
                </Card>
                
                {/* Download Button - Outside the card so it's not captured */}
                <Button
                  onClick={downloadPaymentQR}
                  disabled={downloadingQr}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  {downloadingQr ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Download Payment QR
                </Button>

                {/* Payment Status */}
                <Card className="p-4 bg-slate-800/50 border-slate-700">
                  <h3 className="text-sm font-semibold text-slate-300 mb-3">Payment Status</h3>
                  <div className="flex gap-3">
                    <div
                      onClick={() => setPaymentStatus("pending")}
                      className={`flex-1 p-3 border-2 rounded-lg cursor-pointer transition text-center ${
                        paymentStatus === "pending"
                          ? "border-amber-500 bg-amber-500/10"
                          : "border-slate-600 hover:border-slate-500"
                      }`}
                    >
                      <AlertCircle className={`w-5 h-5 mx-auto mb-1 ${paymentStatus === "pending" ? "text-amber-400" : "text-slate-400"}`} />
                      <p className={`text-sm font-medium ${paymentStatus === "pending" ? "text-amber-400" : "text-slate-400"}`}>Pending</p>
                    </div>
                    <div
                      onClick={() => setPaymentStatus("received")}
                      className={`flex-1 p-3 border-2 rounded-lg cursor-pointer transition text-center ${
                        paymentStatus === "received"
                          ? "border-green-500 bg-green-500/10"
                          : "border-slate-600 hover:border-slate-500"
                      }`}
                    >
                      <CheckCircle className={`w-5 h-5 mx-auto mb-1 ${paymentStatus === "received" ? "text-green-400" : "text-slate-400"}`} />
                      <p className={`text-sm font-medium ${paymentStatus === "received" ? "text-green-400" : "text-slate-400"}`}>Received</p>
                    </div>
                  </div>
                </Card>

                {/* Submit Buttons */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowReview(false)}
                    className="flex-1 border-slate-600 text-slate-300"
                  >
                    Back to Edit
                  </Button>
                  <Button
                    onClick={() => handleSubmit(false)}
                    disabled={submitting}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    {submitting ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Complete Registration
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4 md:p-8">
      {/* Duplicate/pending-registration prompt (shared — see duplicatePromptDialog).
          Fires when the backend returns 409 pending_registration_exists. Renders
          here AND on the review/QR screen so it always appears where the admin
          clicked "Complete Registration". */}
      {duplicatePromptDialog}

      {/* Success Dialog */}
      <Dialog open={successDialog.open} onOpenChange={(open) => {
        if (!open) {
          setSuccessDialog({ open: false, data: null });
          navigate("/admin/registration-pipeline");
        }
      }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-400">
              <CheckCircle className="w-6 h-6" />
              Registration Successful!
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {successDialog.data?.message}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-slate-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-slate-300 mb-3">Child Membership Details</h4>
              <div className="space-y-3">
                {successDialog.data?.children?.map((child, idx) => (
                  <div key={idx} className="bg-slate-900 rounded p-3 border border-slate-700">
                    <div className="font-medium text-white mb-2">{child.name}</div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-slate-400">Membership #:</span>
                        <div className="flex items-center gap-2 mt-1">
                          <code className="bg-amber-500/20 text-amber-400 px-2 py-1 rounded font-mono text-lg">
                            {child.membership_number}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(child.membership_number, `mem-${idx}`)}
                            className="h-7 w-7 p-0"
                          >
                            {copiedField === `mem-${idx}` ? (
                              <Check className="h-4 w-4 text-emerald-400" />
                            ) : (
                              <Copy className="h-4 w-4 text-slate-400" />
                            )}
                          </Button>
                        </div>
                      </div>
                      <div>
                        <span className="text-slate-400">Temp Password:</span>
                        <div className="flex items-center gap-2 mt-1">
                          <code className="bg-slate-700 px-2 py-1 rounded font-mono text-sm text-white">
                            {child.temporary_password}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(child.temporary_password, `pass-${idx}`)}
                            className="h-7 w-7 p-0"
                          >
                            {copiedField === `pass-${idx}` ? (
                              <Check className="h-4 w-4 text-emerald-400" />
                            ) : (
                              <Copy className="h-4 w-4 text-slate-400" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {successDialog.data?.is_new_parent && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-sm">
                <p className="text-blue-300">
                  {sendWelcomeEmail 
                    ? "A welcome email with login instructions has been sent to the parent."
                    : "Remember to share the membership details with the parent."}
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              onClick={() => {
                setSuccessDialog({ open: false, data: null });
                navigate("/admin/registration-pipeline");
              }}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">Register on Behalf of Parent</h1>
            <p className="text-slate-400 text-sm">Create parent account and register children for academy sessions</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Email Lookup Section */}
          <Card className="p-6 bg-gradient-to-r from-blue-900/30 to-purple-900/30 border-blue-700/50">
            <div className="flex items-center gap-2 mb-4">
              <Search className="w-5 h-5 text-blue-400" />
              <h2 className="text-lg font-semibold text-white">Check Existing User</h2>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              Enter parent&apos;s email to check if they already have an account. This will load their details and any existing children.
            </p>
            
            <div className="flex gap-3">
              <Input
                type="email"
                value={lookupEmail}
                onChange={(e) => setLookupEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleEmailLookup()}
                placeholder="parent@example.com"
                className="flex-1 bg-slate-900/50 border-slate-600"
              />
              <Button
                onClick={handleEmailLookup}
                disabled={lookupLoading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {lookupLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Check
                  </>
                )}
              </Button>
            </div>

            {/* Lookup Result */}
            {lookupResult && (
              <div className="mt-4">
                {lookupResult.found ? (
                  <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-400" />
                        <span className="text-emerald-400 font-medium">User Found</span>
                      </div>
                      <Button
                        onClick={handleLoadUserDetails}
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Load Details
                      </Button>
                    </div>
                    <div className="text-sm text-slate-300">
                      <p><strong>Name:</strong> {lookupResult.user.given_name} {lookupResult.user.last_name}</p>
                      <p><strong>Email:</strong> {lookupResult.user.email}</p>
                      {/* Feb 2026 fix — surface membership status loud and
                          early so the admin can see at-a-glance that DM is
                          being honoured on the next registration. The earlier
                          bug was the Review screen quietly skipping the 10%
                          DM discount because the lookup endpoint never
                          returned `is_digital_member`. */}
                      <div className="flex items-center gap-2 flex-wrap mt-2">
                        {lookupResult.user.is_scc_member && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" data-testid="lookup-badge-scc">
                            ✓ SCC Member
                          </span>
                        )}
                        {lookupResult.user.is_digital_member ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-sky-500/20 text-sky-200 border border-sky-500/40" data-testid="lookup-badge-dm-active">
                            ✓ Active Digital Membership
                            {lookupResult.user.digital_membership_expires_at && (
                              <span className="text-sky-300/80">
                                (until {new Date(lookupResult.user.digital_membership_expires_at).toLocaleDateString()})
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-slate-500/20 text-slate-300 border border-slate-500/40" data-testid="lookup-badge-dm-none">
                            No active Digital Membership
                          </span>
                        )}
                      </div>
                      {lookupResult.existing_children?.length > 0 && (
                        <p className="mt-2 text-amber-400">
                          <strong>{lookupResult.existing_children.length} existing child(ren)</strong> found in system
                        </p>
                      )}
                      {lookupResult.pending_payments?.length > 0 && (
                        <p className="text-red-400">
                          <AlertTriangle className="w-4 h-4 inline mr-1" />
                          {lookupResult.pending_payments.length} pending payment(s)
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-slate-800/50 border border-slate-600 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-300">
                      <AlertCircle className="w-5 h-5 text-slate-400" />
                      <span>No existing user found. Proceed with new registration.</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Existing Children Panel */}
            {showExistingChildren && lookupResult?.existing_children?.length > 0 && (
              <div className="mt-4 p-4 bg-slate-800/50 border border-slate-600 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-slate-300">Existing Children (click to re-enroll)</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowExistingChildren(false)}
                    className="text-slate-400"
                  >
                    Hide
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {lookupResult.existing_children.map((child, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleAddExistingChild(child)}
                      className="p-3 bg-slate-900/50 border border-slate-600 rounded-lg cursor-pointer hover:border-blue-500 transition"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-white">{child.name}</p>
                          <p className="text-xs text-slate-400">
                            {child.birthdate} • {child.membership_number}
                          </p>
                          {child.term_info && (
                            <p className="text-xs text-blue-400 mt-1">
                              Last: {child.term_info.name}
                            </p>
                          )}
                        </div>
                        <Plus className="w-5 h-5 text-blue-400" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* Parent Information */}
          <Card className="p-6 bg-slate-800/50 border-slate-700">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5 text-blue-400" />
              <h2 className="text-lg font-semibold text-white">Parent Information</h2>
              {existingUserId && (
                <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">
                  Existing User
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Given Name *</label>
                <Input
                  value={parentGivenName}
                  onChange={(e) => setParentGivenName(e.target.value)}
                  placeholder="John"
                  className="bg-slate-900/50 border-slate-600"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Last Name *</label>
                <Input
                  value={parentLastName}
                  onChange={(e) => setParentLastName(e.target.value)}
                  placeholder="Doe"
                  className="bg-slate-900/50 border-slate-600"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email *
                </label>
                <Input
                  type="email"
                  value={parentEmail}
                  onChange={(e) => setParentEmail(e.target.value)}
                  placeholder="parent@example.com"
                  className="bg-slate-900/50 border-slate-600"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Phone *
                </label>
                <Input
                  value={parentPhone}
                  onChange={(e) => setParentPhone(e.target.value)}
                  placeholder="+65 9123 4567"
                  className="bg-slate-900/50 border-slate-600"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-slate-400 mb-1">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Address
                </label>
                <Input
                  value={parentAddress}
                  onChange={(e) => setParentAddress(e.target.value)}
                  placeholder="Block 123, Street Name, Singapore 123456"
                  className="bg-slate-900/50 border-slate-600"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Nationality</label>
                <Input
                  value={parentNationality}
                  onChange={(e) => setParentNationality(e.target.value)}
                  placeholder="Singaporean"
                  className="bg-slate-900/50 border-slate-600"
                />
              </div>
              <div className="md:col-span-2 flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={isSccMember}
                      onCheckedChange={(v) => setIsSccMember(!!v)}
                      id="is-member"
                      data-testid="admin-register-scc-member-checkbox"
                    />
                    <label htmlFor="is-member" className="text-sm text-slate-300 cursor-pointer">
                      SCC Member (discount applies only with a valid membership #)
                    </label>
                    <details className="inline-block" data-testid="admin-scc-definition-help">
                      <summary className="text-xs text-blue-400/80 cursor-pointer hover:text-blue-300 select-none list-none" title="Who counts as a member?">ⓘ</summary>
                      <div className="absolute z-20 mt-1 w-80 text-xs text-slate-200 bg-slate-900 border border-slate-700 rounded-md p-3 shadow-xl space-y-1.5">
                        <p><strong className="text-blue-300">SCC Club Member</strong> — holds an SCC number (e.g. <span className="font-mono">P0354</span>, <span className="font-mono">JP1598</span>, <span className="font-mono">HN1231</span>, Life <span className="font-mono">PL0354</span>, family <span className="font-mono">HFM/WFM/FFM/MFM</span>). 10% once verified.</p>
                        <p><strong className="text-teal-300">Digital Member</strong> — bought Academy DM; same 10% if valid at the term's start date.</p>
                        <p><strong className="text-slate-400">Non-member</strong> — neither; full fees.</p>
                        <p className="text-slate-400 italic">Format check only, not a live SCC lookup. Admin/system numbers like <span className="font-mono">SCC###</span>, <span className="font-mono">SCC-1E91FBDA</span>, <span className="font-mono">CT…</span>, <span className="font-mono">T####</span> are NOT SCC members.</p>
                      </div>
                    </details>
                  </div>
                  {isSccMember && (
                    <Input
                      value={membershipNumber}
                      onChange={(e) => setMembershipNumber(e.target.value)}
                      placeholder="Membership #"
                      className="bg-slate-900/50 border-slate-600 w-44"
                      data-testid="admin-register-membership-number"
                    />
                  )}
                  {isSccMember && isFamilyMembership && (
                    <div className="flex flex-col">
                      <label className="text-[11px] text-slate-400 mb-0.5">Main SCC Member's last name</label>
                      <Input
                        value={primaryMemberLastName}
                        onChange={(e) => setPrimaryMemberLastName(e.target.value)}
                        placeholder="e.g. Pereira"
                        className="bg-slate-900/50 border-slate-600 w-56"
                        data-testid="admin-register-primary-member-last-name"
                      />
                    </div>
                  )}
                </div>
                {isSccMember && !(membershipNumber || "").trim() && !membershipStatus.entitled && (
                  <div className="text-xs text-slate-400" data-testid="admin-register-membership-no-number">
                    Enter the club membership number to apply the member discount — the checkbox alone
                    does not grant it.
                  </div>
                )}
                {/* One answer, not three. Club verification, the typed number
                    and Digital Membership used to each print their own green or
                    amber line, so the screen could simultaneously say "we
                    couldn't validate this number", "SCC-verified" and "holds a
                    Digital Membership" — with no way to tell which one actually
                    set the price. This states the outcome and its source. */}
                <div
                  className={`text-xs rounded px-2 py-1.5 border ${
                    membershipStatus.percent > 0 || membershipStatus.pending
                      ? "text-emerald-200 bg-emerald-500/10 border-emerald-500/30"
                      : "text-slate-300 bg-slate-800/60 border-slate-700"
                  }`}
                  data-testid="admin-register-membership-status"
                >
                  <p className="font-semibold">
                    {membershipStatus.pending
                      ? `${membershipStatus.label} — pick a term to see the discount`
                      : membershipStatus.percent > 0
                        ? `${membershipStatus.label} — ${membershipStatus.percent}% off this registration`
                        : "No member discount — full fees"}
                  </p>
                  {membershipStatus.reasons.map((reason) => (
                    <p key={reason} className="text-slate-300/90 mt-0.5">· {reason}</p>
                  ))}
                  {membershipValidating && (
                    <p className="text-slate-400 mt-0.5">· Checking the number…</p>
                  )}
                  {parentStoredVerified && (
                    <span className="hidden" data-testid="parent-verified-note" />
                  )}
                  {parentAlreadyHasDM && (
                    <span className="hidden" data-testid="parent-dm-note" />
                  )}
                </div>
                {parentPortalRef && (
                  <div className="text-[11px] text-slate-400" data-testid="parent-portal-ref">
                    Portal reference <span className="font-mono text-slate-300">{parentPortalRef}</span>{" "}
                    — issued by this portal when the account was approved. It is not an SCC club
                    membership number, so it is not used for the member discount.
                  </div>
                )}
              </div>
              {/* Feb 2026 fix — Digital Membership toggle (per parent).
                  Reads digital_membership_config.annual_price (canonical)
                  with fallback to .price for backwards compat. */}
              {(() => {
                const dmCfg = rateTable?.digital_membership_config || {};
                const dmPrice = Number(dmCfg.annual_price ?? dmCfg.price ?? 0);
                const dmEnabledOnRT = !!dmCfg.enabled && dmPrice > 0;
                return (
                  <div className="flex items-center gap-2 col-span-1 md:col-span-2 mt-2 p-3 rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/5">
                    <Checkbox
                      checked={purchaseDigitalMembership && !parentAlreadyHasDM && dmEnabledOnRT}
                      onCheckedChange={(v) => setPurchaseDigitalMembership(!!v)}
                      id="purchase-dm"
                      disabled={parentAlreadyHasDM || !dmEnabledOnRT}
                      data-testid="admin-register-purchase-dm-checkbox"
                    />
                    <label htmlFor="purchase-dm" className="text-sm cursor-pointer flex-1">
                      <span className="text-fuchsia-200 font-medium">Purchase Digital Membership for parent</span>
                      {dmEnabledOnRT ? (
                        <span className="text-fuchsia-300/80 ml-2">
                          (${dmPrice.toFixed(2)} / year)
                        </span>
                      ) : (
                        <span className="text-amber-400 ml-2 text-xs">
                          — DM is disabled or has no annual_price configured on this term&apos;s rate table
                        </span>
                      )}
                      {parentAlreadyHasDM && (
                        <span className="text-emerald-400 ml-2 text-xs">— parent already has active DM</span>
                      )}
                    </label>
                  </div>
                );
              })()}
            </div>
          </Card>

          {/* Term Selection */}
          <Card className="p-6 bg-slate-800/50 border-slate-700">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-5 h-5 text-purple-400" />
              <h2 className="text-lg font-semibold text-white">Select Term</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {terms.map(term => (
                <div
                  key={term.id}
                  data-testid={`term-option-${term.id}`}
                  onClick={() => setSelectedTermId(term.id)}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition ${
                    selectedTermId === term.id
                      ? "border-purple-500 bg-purple-500/10"
                      : "border-slate-600 hover:border-slate-500"
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-white">{term.name}</p>
                      <p className="text-sm text-slate-400">
                        {term.start_date?.split("T")[0]} - {term.end_date?.split("T")[0]}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {term.duration_value || term.training_weeks} weeks
                      </p>
                    </div>
                    {term.is_hidden && (
                      <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded">
                        Hidden
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Children */}
          <Card className="p-6 bg-slate-800/50 border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-green-400" />
                <h2 className="text-lg font-semibold text-white">Children</h2>
              </div>
              <Button
                onClick={addChild}
                size="sm"
                className="bg-green-600 hover:bg-green-700"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Child
              </Button>
            </div>

            <div className="space-y-6">
              {children.map((child, childIndex) => {
                const childAge = calculateAge(child.birthdate);
                const termWeeks = selectedTerm?.duration_unit === "weeks" 
                  ? (selectedTerm?.duration_value || 10)
                  : (selectedTerm?.training_weeks || 10);
                const duplicateWarning = duplicateWarnings[childIndex];

                return (
                  <div key={childIndex} className="p-4 bg-slate-900/50 rounded-lg border border-slate-600">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Baby className="w-4 h-4 text-blue-400" />
                        <span className="font-medium text-white">
                          Child {childIndex + 1}
                          {child.given_name && ` - ${child.given_name}`}
                          {childAge !== null && ` (${childAge} yrs)`}
                        </span>
                        {child.is_existing && (
                          <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">
                            Re-enrolling
                          </span>
                        )}
                      </div>
                      {children.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeChild(childIndex)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>

                    {/* Duplicate Warning */}
                    {duplicateWarning && (
                      <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                          <div className="text-sm">
                            <p className="text-amber-400 font-medium">Possible Duplicate</p>
                            <p className="text-slate-300">{duplicateWarning.message}</p>
                            {duplicateWarning.existing_child?.parent_info && (
                              <p className="text-slate-400 text-xs mt-1">
                                Registered under: {duplicateWarning.existing_child.parent_info.email}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Given Name *</label>
                        <Input
                          value={child.given_name}
                          onChange={(e) => updateChild(childIndex, "given_name", e.target.value)}
                          placeholder="First name"
                          className="bg-slate-800 border-slate-600 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Last Name *</label>
                        <Input
                          value={child.last_name}
                          onChange={(e) => updateChild(childIndex, "last_name", e.target.value)}
                          placeholder="Last name"
                          className="bg-slate-800 border-slate-600 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Date of Birth *</label>
                        <Input
                          type="date"
                          value={child.birthdate}
                          onChange={(e) => updateChild(childIndex, "birthdate", e.target.value)}
                          className="bg-slate-800 border-slate-600 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Gender</label>
                        <select
                          value={child.gender}
                          onChange={(e) => updateChild(childIndex, "gender", e.target.value)}
                          className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-md text-sm text-white"
                        >
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">School</label>
                        <Input
                          value={child.school}
                          onChange={(e) => updateChild(childIndex, "school", e.target.value)}
                          placeholder="School name"
                          className="bg-slate-800 border-slate-600 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Nationality</label>
                        <Input
                          value={child.nationality}
                          onChange={(e) => updateChild(childIndex, "nationality", e.target.value)}
                          placeholder="Nationality"
                          className="bg-slate-800 border-slate-600 text-sm"
                        />
                      </div>
                    </div>

                    {/* Contact Info (NEW) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">
                          <Mail className="w-3 h-3 inline mr-1" />
                          Child&apos;s Email (optional)
                        </label>
                        <Input
                          type="email"
                          value={child.email}
                          onChange={(e) => updateChild(childIndex, "email", e.target.value)}
                          placeholder="child@example.com"
                          className="bg-slate-800 border-slate-600 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">
                          <Phone className="w-3 h-3 inline mr-1" />
                          Child&apos;s Phone (optional)
                        </label>
                        <Input
                          value={child.phone}
                          onChange={(e) => updateChild(childIndex, "phone", e.target.value)}
                          placeholder="+65 9123 4567"
                          className="bg-slate-800 border-slate-600 text-sm"
                        />
                      </div>
                    </div>

                    {/* Medical & Emergency */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Medical Conditions</label>
                        <Input
                          value={child.medical_conditions}
                          onChange={(e) => updateChild(childIndex, "medical_conditions", e.target.value)}
                          placeholder="None / Asthma / etc."
                          className="bg-slate-800 border-slate-600 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Emergency Contact Name</label>
                        <Input
                          value={child.emergency_contact_name}
                          onChange={(e) => updateChild(childIndex, "emergency_contact_name", e.target.value)}
                          placeholder={`${parentGivenName} ${parentLastName}`.trim() || "Parent name"}
                          className="bg-slate-800 border-slate-600 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Emergency Contact Phone</label>
                        <Input
                          value={child.emergency_contact}
                          onChange={(e) => updateChild(childIndex, "emergency_contact", e.target.value)}
                          placeholder={parentPhone || "+65 9123 4567"}
                          className="bg-slate-800 border-slate-600 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Relationship</label>
                        <select
                          value={child.emergency_relationship}
                          onChange={(e) => updateChild(childIndex, "emergency_relationship", e.target.value)}
                          className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-md text-sm text-white"
                        >
                          <option value="Parent">Parent</option>
                          <option value="Guardian">Guardian</option>
                          <option value="Grandparent">Grandparent</option>
                          <option value="Sibling">Sibling</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>

                    {/* Session Selection */}
                    {selectedTermId && sessions.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-slate-700">
                        <p className="text-sm font-medium text-slate-300 mb-3">
                          Select Sessions for {child.given_name || `Child ${childIndex + 1}`}:
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {sessions.map(session => {
                            const isSelected = child.session_ids.includes(session.id);
                            // In Fixed Term (Max Capacity) mode the billable rate
                            // comes from the rate table, not the session record —
                            // so display that instead of a possibly-stale $ price.
                            const mcModeActive = rateTable?.pricing_mode === "max_capacity";
                            const displaySessionPrice = mcModeActive
                              ? (parseFloat(rateTable?.max_capacity_price_per_session) || session.price)
                              : session.price;
                            const sessionTotal = isSelected ? (displaySessionPrice * termWeeks) : 0;
                            
                            if (childAge !== null && session.min_age && session.max_age) {
                              if (childAge < session.min_age || childAge > session.max_age) {
                                return null;
                              }
                            }

                            return (
                              <div
                                key={session.id}
                                data-testid={`child-${childIndex}-session-card-${session.id}`}
                                onClick={() => toggleSessionForChild(childIndex, session.id)}
                                className={`p-3 border rounded-lg cursor-pointer transition text-sm ${
                                  isSelected
                                    ? "border-blue-500 bg-blue-500/10"
                                    : "border-slate-600 hover:border-slate-500"
                                }`}
                              >
                                <div className="flex items-start gap-2">
                                  <Checkbox
                                    checked={isSelected}
                                    onCheckedChange={() => toggleSessionForChild(childIndex, session.id)}
                                    className="mt-0.5"
                                  />
                                  <div className="flex-1">
                                    <p className="font-medium text-white">
                                      {session.age_group_name && (
                                        <span className="text-blue-400">[{session.age_group_name}] </span>
                                      )}
                                      {getDayName(session.day_of_week)} {session.start_time} - {session.end_time}
                                    </p>
                                    <p className="text-xs text-slate-400" data-testid={`child-${childIndex}-session-${session.id}-price`}>
                                      {session.location || "Main Field"} • ${Number(displaySessionPrice).toFixed(2)}/session
                                    </p>
                                    {session.session_type && (
                                      <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded ${
                                        session.session_type === 'game' 
                                          ? 'bg-purple-500/20 text-purple-400' 
                                          : session.session_type === 'competitive'
                                          ? 'bg-orange-500/20 text-orange-400'
                                          : 'bg-blue-500/20 text-blue-400'
                                      }`}>
                                        {session.session_type === 'game' ? 'Game Day' : 
                                         session.session_type === 'competitive' ? 'Competitive' : 
                                         'Training'}
                                      </span>
                                    )}
                                    {isSelected && (
                                      <p className="text-xs text-emerald-400 mt-1 font-medium" data-testid={`child-${childIndex}-session-${session.id}-line-total`}>
                                        ✓ 1 session × {termWeeks} weeks = ${sessionTotal.toFixed(2)}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {child.session_ids.length > 0 && (
                          <p className="text-sm text-emerald-400 mt-2 font-medium">
                            {child.session_ids.length} session(s) selected
                          </p>
                        )}

                        {/* Feb 2026 fix — per-child Sign-up Registration Fee.
                            Admin opts in/out per child. Default is ON for new
                            kids and OFF for re-enrolled kids (already paid). */}
                        {rateTable?.registration_fee?.enabled && rateTable?.registration_fee?.amount > 0 && child.session_ids.length > 0 && (
                          <div className="mt-3 flex items-start gap-2 p-3 rounded-lg border border-cyan-500/30 bg-cyan-500/5">
                            <input
                              type="checkbox"
                              id={`reg-fee-${childIndex}`}
                              data-testid={`child-${childIndex}-charge-registration-fee`}
                              checked={!!child.charge_registration_fee}
                              onChange={(e) => {
                                const updated = [...children];
                                updated[childIndex] = { ...updated[childIndex], charge_registration_fee: e.target.checked };
                                setChildren(updated);
                              }}
                              className="mt-0.5 cursor-pointer accent-cyan-500"
                            />
                            <label htmlFor={`reg-fee-${childIndex}`} className="text-sm cursor-pointer flex-1">
                              <span className="text-cyan-200 font-medium">
                                Charge one-time Sign-up Registration Fee (${Number(rateTable.registration_fee.amount).toFixed(2)})
                              </span>
                              <p className="text-xs text-cyan-300/70 mt-0.5">
                                {child.is_existing
                                  ? "Off only when an earlier registration was fully paid. Tick to charge it."
                                  : "Default ON for new kids. Untick if this child has already paid the registration fee."}
                              </p>
                            </label>
                          </div>
                        )}

                        {/* Sprint 1 v2 (May 2026) — per-session billable
                            override. Each session line (Wed/Sat/Sun-games)
                            can be adjusted independently. Reason required
                            when ANY override differs from term_weeks.
                            Shown for ALL pricing modes: on Fixed Term (Max
                            Capacity) the billed value is treated as the number
                            of WEEKS billed (e.g. a late joiner billed 8 of 9),
                            applied to the fixed weekly package. */}
                        {child.session_ids.length > 0 && (
                          <div className="mt-3 p-4 bg-amber-500/10 border-2 border-amber-500/40 rounded-lg">
                            <div className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                id={`override-toggle-${childIndex}`}
                                data-testid={`child-${childIndex}-override-toggle`}
                                checked={!!(child.session_count_overrides && Object.keys(child.session_count_overrides).length > 0)}
                                onChange={(e) => {
                                  const updated = [...children];
                                  if (e.target.checked) {
                                    // Initialise per-session map: every selected session → term_weeks
                                    const map = {};
                                    (updated[childIndex].session_ids || []).forEach((sid) => {
                                      map[sid] = termWeeks;
                                    });
                                    updated[childIndex] = {
                                      ...updated[childIndex],
                                      session_count_overrides: map,
                                      session_count_override_reason: updated[childIndex].session_count_override_reason || "",
                                      session_count_override: null  // legacy uniform — no longer used
                                    };
                                  } else {
                                    updated[childIndex] = {
                                      ...updated[childIndex],
                                      session_count_overrides: {},
                                      session_count_override: null,
                                      session_count_override_reason: ""
                                    };
                                  }
                                  setChildren(updated);
                                }}
                                className="mt-0.5 w-4 h-4 rounded border-amber-500 cursor-pointer"
                              />
                              <label htmlFor={`override-toggle-${childIndex}`} className="cursor-pointer flex-1">
                                <div className="font-semibold text-amber-200 text-sm">⚙️ Adjust billable sessions per line</div>
                                <div className="text-xs text-amber-200/80 mt-0.5">
                                  Bill each session line individually (e.g. Wed = 9, Sat = 9, Sun games = 7).
                                  Use for back-billing or in-kind credits. Reason appears on the invoice.
                                </div>
                              </label>
                            </div>

                            {!!(child.session_count_overrides && Object.keys(child.session_count_overrides).length > 0) && (
                              <div className="mt-3 space-y-2">
                                <div className="text-xs text-amber-200/70 uppercase tracking-wide font-medium px-1">
                                  Selected sessions for this child
                                </div>
                                {child.session_ids.map((sid) => {
                                  const sess = sessions.find((s) => s.id === sid);
                                  if (!sess) return null;
                                  const billed = child.session_count_overrides?.[sid] ?? termWeeks;
                                  const isAdjusted = billed !== termWeeks;
                                  // Fixed Term (Max Capacity): the billable rate comes
                                  // from the rate table, not the (possibly stale) session
                                  // record price. Mirror the session-card display.
                                  const mcModeActive = rateTable?.pricing_mode === "max_capacity";
                                  const rowPrice = mcModeActive
                                    ? (parseFloat(rateTable?.max_capacity_price_per_session) || sess.price || 0)
                                    : (sess.price || 0);
                                  return (
                                    <div
                                      key={sid}
                                      className={`flex items-center gap-3 p-2 rounded ${isAdjusted ? "bg-amber-600/20 border border-amber-500/50" : "bg-slate-900/60 border border-slate-700"}`}
                                      data-testid={`child-${childIndex}-session-${sid}-row`}
                                    >
                                      <div className="flex-1 min-w-0">
                                        <div className="text-sm text-white truncate">
                                          {sess.day_of_week !== undefined && sess.day_of_week !== null ? `${getDayName(sess.day_of_week)} ` : ""}
                                          {sess.start_time && sess.end_time ? `${sess.start_time} - ${sess.end_time}` : (sess.name || sess.session_name || "Session")}
                                        </div>
                                        <div className="text-xs text-slate-400">
                                          {(sess.session_type || "training").toString().toLowerCase()} · ${rowPrice.toFixed(2)}/session
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2 flex-shrink-0">
                                        <span className="text-xs text-slate-400 whitespace-nowrap">Bill:</span>
                                        <Input
                                          type="number"
                                          min="1"
                                          max={termWeeks * 2}
                                          value={billed}
                                          data-testid={`child-${childIndex}-session-${sid}-count`}
                                          onChange={(e) => {
                                            const v = parseInt(e.target.value, 10);
                                            const updated = [...children];
                                            const map = { ...(updated[childIndex].session_count_overrides || {}) };
                                            map[sid] = Number.isFinite(v) && v >= 1 ? v : 1;
                                            updated[childIndex] = {
                                              ...updated[childIndex],
                                              session_count_overrides: map
                                            };
                                            setChildren(updated);
                                          }}
                                          className="w-20 bg-slate-800 border-slate-600 text-white text-center"
                                        />
                                        <span className="text-xs text-slate-500 whitespace-nowrap">/ {termWeeks}</span>
                                        <span className={`text-xs font-mono whitespace-nowrap ${isAdjusted ? "text-amber-300" : "text-slate-400"}`}>
                                          = ${(rowPrice * billed).toFixed(2)}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })}

                                <div className="mt-3">
                                  <Label htmlFor={`override-reason-${childIndex}`} className="text-xs text-amber-200">
                                    Reason (required when any line is adjusted; appears on invoice)
                                  </Label>
                                  <Input
                                    id={`override-reason-${childIndex}`}
                                    data-testid={`child-${childIndex}-override-reason`}
                                    type="text"
                                    value={child.session_count_override_reason || ""}
                                    onChange={(e) => {
                                      const updated = [...children];
                                      updated[childIndex] = {
                                        ...updated[childIndex],
                                        session_count_override_reason: e.target.value
                                      };
                                      setChildren(updated);
                                    }}
                                    placeholder="e.g. Term 1 had 7 games instead of 9"
                                    className="mt-1 bg-slate-800 border-slate-600 text-white"
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Kit selection */}
          {selectedTermId && kitChildrenList.length > 0 && (
            <Card className="p-6 bg-slate-800/50 border-slate-700" data-testid="admin-kit-card">
              <KitSelectionStep
                termId={selectedTermId}
                childrenList={kitChildrenList}
                onChange={(sel, total) => { setKitSelections(sel); setKitExtrasTotal(total); }}
              />
            </Card>
          )}

          {/* Fee Summary */}
          <Card className="p-6 bg-slate-800/50 border-slate-700">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="w-5 h-5 text-amber-400" />
              <h2 className="text-lg font-semibold text-white">Fee Calculation</h2>
            </div>

            {feeBreakdown && feeBreakdown.childBreakdowns.length > 0 ? (
              <div className="space-y-4">
                {feeBreakdown.childBreakdowns.map((child, idx) => (
                  <div key={idx} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700">
                    <p className="font-medium text-white mb-2">{child.name}</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between text-slate-400">
                        <span>
                          {child.isMaxCapacity
                            ? `${child.billedSessionsPerWeek} session(s)/week billed (Fixed Term)`
                            : `${child.sessionCount} session(s)/week`}
                        </span>
                        <span>
                          {child.originalPricePerSession > child.pricePerSession && (
                            <span className="line-through text-slate-500 mr-2">${child.originalPricePerSession.toFixed(2)}</span>
                          )}
                          <span className="text-emerald-400 font-medium">${fmtRate(child.pricePerSessionExact ?? child.pricePerSession)}/session</span>
                        </span>
                      </div>
                      <div className="flex justify-between text-slate-300">
                        <span>
                          {child.isMaxCapacity
                            ? `${child.billedSessionsPerWeek} × $${fmtRate(child.pricePerSessionExact ?? child.pricePerSession)} × ${child.plannedWeeks} weeks`
                            : `${child.sessionCount} × $${fmtRate(child.pricePerSessionExact ?? child.pricePerSession)} × ${child.billedWeeks !== null ? `${child.billedWeeks} ${child.billedWeeks !== child.plannedWeeks ? "session(s) *" : "weeks"}` : `varies *`}`}
                        </span>
                        <span>${child.finalFee.toFixed(2)}</span>
                      </div>
                      {child.isMaxCapacity && child.sessionCount !== child.billedSessionsPerWeek && (
                        <div className="text-xs text-amber-400 italic pt-1">
                          Fixed Term (Max Capacity): billed for {child.billedSessionsPerWeek} sessions/week regardless of the {child.sessionCount} picked.
                        </div>
                      )}
                      {(child.hasPerSessionOverride || (child.sessionCountOverride && child.billedWeeks !== child.plannedWeeks)) && (
                        <div className="text-xs text-amber-400 italic pt-1">
                          * {child.hasPerSessionOverride ? `Per-session override applied (${child.billedSummary} of ${child.plannedWeeks})` : `Adjusted from ${child.plannedWeeks} to ${child.billedWeeks} sessions`}
                          {child.sessionCountOverrideReason ? ` — ${child.sessionCountOverrideReason}` : " — (reason required)"}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {feeBreakdown.cancellationOffsetTotal > 0 && (
                  <div className="flex justify-between text-emerald-400 text-sm px-1" data-testid="fee-line-cancelled-session">
                    <span>
                      🗓️ Cancelled session not billed
                      {feeBreakdown.cancellationNotes?.length > 0
                        ? ` (${feeBreakdown.cancellationNotes.join(", ")})`
                        : ""}
                    </span>
                    <span>-${feeBreakdown.cancellationOffsetTotal.toFixed(2)} included</span>
                  </div>
                )}

                {feeBreakdown.siblingDiscountPercent > 0 && (
                  <div className="flex justify-between text-emerald-400 text-sm px-1">
                    <span>👨‍👩‍👧‍👦 Sibling Discount ({feeBreakdown.siblingDiscountPercent}%)</span>
                    <span>Savings of ${feeBreakdown.siblingDiscountSavings.toFixed(2)} included</span>
                  </div>
                )}

                {/* Feb 2026 fix — surface the membership-tier discount that
                    was applied per-child (so admin sees DM 10% / club 10% /
                    stack-mode total reflected in the per-child rate). Without
                    this the eldest child's $42.30/session looked like the
                    "default" rate even though it was discounted from $47. */}
                {feeBreakdown.memberDiscountPercent > 0 && (
                  <div className="flex justify-between text-sky-300 text-sm px-1" data-testid="fee-line-member-discount">
                    <span>🪪 Membership Discount ({feeBreakdown.memberDiscountPercent}%) — applied per-session</span>
                    <span>
                      Savings of $
                      {feeBreakdown.childBreakdowns
                        .reduce((sum, c) => sum + (c.memberDiscount || 0), 0)
                        .toFixed(2)}{" "}
                      included
                    </span>
                  </div>
                )}

                {/* Feb 2026 fix — surface Sign-up Registration Fee + DM line items */}
                {feeBreakdown.regFeeTotal > 0 && (
                  <div className="flex justify-between text-cyan-300 text-sm px-1" data-testid="fee-line-registration">
                    <span>📝 Sign-up Registration Fee ({Math.round(feeBreakdown.regFeeTotal / feeBreakdown.regFeePerChild)} × ${feeBreakdown.regFeePerChild.toFixed(2)})</span>
                    <span>${feeBreakdown.regFeeTotal.toFixed(2)}</span>
                  </div>
                )}
                {feeBreakdown.dmPrice > 0 && (
                  <div className="flex justify-between text-fuchsia-300 text-sm px-1" data-testid="fee-line-dm">
                    <span>💳 Digital Membership (1 year)</span>
                    <span>${feeBreakdown.dmPrice.toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between items-center pt-3 border-t border-slate-600">
                  <span className="text-lg font-semibold text-slate-300">Total Fee:</span>
                  <span className="text-2xl font-bold text-emerald-400" data-testid="admin-register-grand-total">${calculatedFee.toFixed(2)}</span>
                </div>
              </div>
            ) : (
              <div className="text-center text-slate-400 py-4">
                Select sessions for children to see fee calculation
              </div>
            )}
          </Card>

          {/* Options */}
          <Card className="p-6 bg-slate-800/50 border-slate-700">
            <h2 className="text-lg font-semibold text-white mb-4">Additional Options</h2>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={sendWelcomeEmail}
                  onCheckedChange={setSendWelcomeEmail}
                  id="send-email"
                />
                <label htmlFor="send-email" className="text-sm text-slate-300 cursor-pointer">
                  Send welcome email to parent with login instructions
                </label>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1">Admin Notes (internal)</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Any notes about this registration..."
                  rows={3}
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-md text-white text-sm"
                />
              </div>
            </div>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleShowReview}
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Eye className="w-4 h-4 mr-2" />
              Review & Generate QR
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}